import axios from 'axios';

export const getPortfolioAccounts = (
    contactId: string | number,
    portfolioId: string | number,
) => axios.get(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.0/contacts/${contactId}/portfolios/${portfolioId}/accounts?language=de`,
);

export const postFoundingsSearch = (contactId: string | number, body) => axios.post(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.1/contacts/${contactId}/fundings/search`,
    body,
);

export const getPortfolioProposals = (
    contactId: string | number,
    portfolioProposalId: string | number,
) => axios.get(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.1/contacts/${contactId}/portfolioproposals/${portfolioProposalId}?language=de   `,
);

export const postTopUp = ({
    contactId,
    portfolioId,
    body,
}: {
    contactId: string | number,
    portfolioId: string | number,
    body: TTopUpBody,
}) => axios.post(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1.1/contacts/${contactId}/portfolios/${portfolioId}/top-up`,
    body,
);

type TTopUpBody = {
    mTanConfirmation: {
        code: string,
        sessionId: string
    },
    exAnteCostStatement?: string,
    exAnteCostId: string,
    amount: number,
    transactionDate: string | null,
    userAnswers: {
        question: string,
        answers: string[],
        answersValue: string
    }[]
}

export const postPayOut = ({
    contactId,
    portfolioId,
    body,
}: {
    contactId: string | number,
    portfolioId: string | number,
    body: PayOutBodyType,
}) => axios.post(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1/contacts/${contactId}/portfolios/${portfolioId}/sell`,
    body,
);

export type PayOutBodyType = {
    mTanConfirmation: {
        code: string,
        sessionId: string
    },
    amount: number,
    exAnteCostId: string,
    exAnteCostStatement?: string
}

export const patchPortfolioFunding = ({
    contactId,
    portfolioId,
    body,
}: {
    contactId: string | number,
    portfolioId: string | number,
    body: PortfolioFundingBody,

}) => axios.patch(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1.1/contacts/${contactId}/portfolios/${portfolioId}/funding`,
    body,
);

export type PortfolioFundingBody = {
    fundingId?: number,
    mTanConfirmation: {
        code: string,
        sessionId: string,
    },
    exAnteCostId: string,
    exAnteCostStatement: string,
    savingsRate: number,
    executionDay: number,
    firstExecutionDate: Date | string,
    recurrenceType: 0, // 3 - Monthly; 5-Bi-monthly; 6-Quarterly; 7-Semi-annually; 8-Annually
    annualIncrease?: 0, // from 1 to 999 %
    startIncreaseDate?: Date | string,
    endDate?: Date | string,
    userAnswers: {
        question: string,
        answers: string[],
        answersValue: string
    }[]
}

export const deletePortfolioFunding = ({
    contactId,
    fundingId,
    body,
}: {
    contactId: string | number,
    fundingId: string,
    body: DeletePortfolio,
}) => axios.delete(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1.0/contacts/${contactId}/fundings/${fundingId}`,
    { data: body },
);

type DeletePortfolio = {
    mTanConfirmation: {
        code: string,
        sessionId: string,
    },
}

export const postModelPortfolioTarget = (
    pseudoIsin: string,
) => axios.post(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1/model-portfolios/${pseudoIsin}/target`,
);

export const getRiskCategories = () => axios.get(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.1/riskcategories?language=de`,
);

export const orderbookSearch = (
    contactId: string | number,
    portfolioId: string | number,
    body: OrderbookSearchBody,
) => axios.post(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.0/contacts/${contactId}/portfolios/${portfolioId}/orderbook `,
    body,
);

export const ORDERBOOK_SEARCH_PAY_OUT_BODY = {
    ORDER_TYPE_ID: 2,
    STATUS_ID: 4,
    PAGE_SIZE: 1000,
};

type OrderbookSearchBody = {
    PortfolioExternalId?: string, // external portfolio Id
    OrderTypeIds: number[],
    OrderBookStatusIds: number[],
    StatusIds?: number[],
    Page: number,
    PageSize: number,
};

export const getPortfolioPerformance = (contactId, portfolioId) => axios.get(
    `${global?.AppConfig?.SERVICES_URL_SUFFIX}/v1.0/contacts/${contactId}/portfolios/${portfolioId}/performance`,
);

export const getMwrPerformance = ({ contactId, portfolioId, range = 'YTD' }) => axios.get(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1.0/contacts/${contactId}/portfolios/${portfolioId}/mwr-performance?range=${range}`,
);

export const getExposure = (
    isin: string,
) => axios.get(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1.0/securities/${isin}/exposure`,
);

export const postTopicSwap = ({ contactId, portfolioId, body }: PostTopicSwap) => axios.post(
    `${global?.AppConfig?.DSL_URL_SUFFIX}/v1/contacts/${contactId}/portfolios/${portfolioId}/topic-swap`,
    body,
);

type PostTopicSwap = {
    contactId: string;
    portfolioId: string;
    body: {
        mTanConfirmation: {
            code: string,
            sessionId: string
        },
        exAnteCostStatement: string,
        exAnteCostId: string,
        newPseudoIsin: string
    }
}

export const getContactPortfolioManagementFee = (
    { contactId, portfolioId }: PortfolioManagementFee,
) => axios.get(`${global?.AppConfig?.DSL_URL_SUFFIX}/v1/contacts/${contactId}/portfolios/${portfolioId}/management-fees`);

type PortfolioManagementFee = {
    contactId: string | number;
    portfolioId: string | number
}

type TPostDeleteScheduledOrderBody = {
    mTanConfirmation: {
        code: string;
        sessionId: string;
    };
}

export const postDeleteScheduledOrder = ({
    contactId,
    portfolioId,
    scheduledOrderId,
    body,
}: {
    contactId: string | number;
    portfolioId: string | number;
    scheduledOrderId: string | number;
    body: TPostDeleteScheduledOrderBody;
}) => axios.post(
    `${global?.AppConfig?.ROUTING_KEY_DSL}/v1.0/contacts/${contactId}/portfolios/${portfolioId}/orders/scheduled/${scheduledOrderId}/cancel`,
    body,
);
